import type { MarketId } from '@morpho-org/blue-sdk'
import type { Address } from 'viem'

import {
  Alert,
  Button,
  cn,
  type ColumnDef,
  DataTableHeader,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  MultiSelect,
  type SortingState,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Text,
  useReactTable
} from '@repo/ui'
import { abbreviateAddress } from '@repo/utils'
import { EtherscanLink, MorphoLink, TokenIcon, useChainId, useUserAccount } from '@repo/web3'
import { RefreshCw } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { LiquidatableResult, LiquidationPosition } from '@/lib/shared/types'

import { formatLTV, formatters } from '@/lib/shared/utils/formatters'

import { useLiquidations } from '../../useLiquidations'
import { CandidateListAge } from '../CandidateListAge'
import { LiquidationModal } from '../LiquidationModal'
import { MarketIdLookup } from '../MarketIdLookup'
import { TokenAmountCell, TokenPairCell } from './cells'
import { ActionsCell } from './cells/ActionsCell'

/**
 * Copy for a zero-row result. Each branch is a distinct outcome that must not collapse into the
 * others: a market that does not exist was never examined, a truncated sweep did not finish, and only
 * the last two are genuine "nothing to liquidate" verdicts.
 */
function emptyResultMessage(data: LiquidatableResult | undefined, isTargeted: boolean): string {
  if (data?.marketFound === false) {
    return 'That market ID is not a market on the connected network. Check the ID, and which network you are on.'
  }
  if (data?.truncated) {
    return 'No liquidatable positions among those checked — discovery hit its page limit before covering everything.'
  }
  if (isTargeted) return 'No liquidatable positions in this market.'
  return 'No liquidatable positions in listed markets. Automatic discovery covers markets listed on Morpho; open Market ID above to check any other market.'
}

export function LiquidationTable() {
  const parentRef = useRef<HTMLDivElement>(null)

  const { userAddress: account } = useUserAccount()
  const chainId = useChainId()

  const [selectedPosition, setSelectedPosition] = useState<LiquidationPosition | undefined>()
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedMarketId, setSelectedMarketId] = useState<MarketId | undefined>()
  const [sorting, setSorting] = useState<SortingState>([])

  const onRowClick = useCallback((pos: LiquidationPosition) => {
    setSelectedPosition(pos)
    setModalOpen(true)
  }, [])

  const { data, refetch, isLoading, isFetching, isError } = useLiquidations(selectedMarketId)
  const allPositions = data?.positions

  const [loanTokenFilter, setLoanTokenFilter] = useState<string[]>([])
  const [collateralTokenFilter, setCollateralTokenFilter] = useState<string[]>([])
  const [marketFilter, setMarketFilter] = useState<string[]>([])
  const [borrowerFilter, setBorrowerFilter] = useState<string[]>([])
  const [previousChainId, setPreviousChainId] = useState(chainId)
  const [previousMarketId, setPreviousMarketId] = useState(selectedMarketId)

  // Reset market ID and filters when chain changes. Filter values are
  // addresses/market IDs from the prior chain and would otherwise hide
  // every row on the new chain. Adjusting during render rather than in an effect
  // keeps the stale filters from reaching the rows for one paint.
  //
  // The selection goes too: the modal reads `useChainId()` live, so a wallet-initiated switch while
  // it is open would pair prior-chain market params and amounts with new-chain contract addresses.
  if (chainId !== previousChainId) {
    setPreviousChainId(chainId)
    setModalOpen(false)
    setSelectedPosition(undefined)
    setSelectedMarketId(undefined)
    setLoanTokenFilter([])
    setCollateralTokenFilter([])
    setMarketFilter([])
    setBorrowerFilter([])
  }

  // Same hazard within one chain: entering a market ID swaps the row set and hides the market
  // `MultiSelect`, so a filter left over from the previous set would exclude every new row with no
  // visible control to clear it.
  if (selectedMarketId !== previousMarketId) {
    setPreviousMarketId(selectedMarketId)
    setLoanTokenFilter([])
    setCollateralTokenFilter([])
    setMarketFilter([])
    setBorrowerFilter([])
  }

  const filteredPositions = useMemo(() => {
    if (!allPositions) return []
    const hasCollateralFilter = collateralTokenFilter.length !== 0
    const hasLoanFilter = loanTokenFilter.length !== 0
    const hasMarketFilter = marketFilter.length !== 0
    const hasBorrowerFilter = borrowerFilter.length !== 0

    return allPositions.filter(position => {
      const collateralMatch =
        !hasCollateralFilter ||
        collateralTokenFilter.includes(position.marketParams.collateralToken)
      const loanMatch = !hasLoanFilter || loanTokenFilter.includes(position.marketParams.loanToken)
      const marketMatch = !hasMarketFilter || marketFilter.includes(position.marketId)
      const borrowerMatch = !hasBorrowerFilter || borrowerFilter.includes(position.user)
      return collateralMatch && loanMatch && marketMatch && borrowerMatch
    })
  }, [allPositions, collateralTokenFilter, loanTokenFilter, marketFilter, borrowerFilter])

  const loanTokenFilterOptions = useMemo(() => {
    if (!allPositions) return []
    const map = new Map<string, { symbol: string; address: Address }>()
    allPositions.forEach(item => {
      const t = item.loanTokenMetadata
      if (t) map.set(t.symbol, { symbol: t.symbol, address: item.marketParams.loanToken })
    })
    return Array.from(map.values()).map(token => ({
      value: token.address,
      keywords: [token.symbol],
      component: (
        <div className="flex items-center gap-2">
          <TokenIcon size="xs" symbol={token.symbol} />
          <span>{token.symbol}</span>
        </div>
      )
    }))
  }, [allPositions])

  const collateralTokenFilterOptions = useMemo(() => {
    if (!allPositions) return []
    const map = new Map<string, { symbol: string; address: Address }>()
    allPositions.forEach(item => {
      const t = item.collateralTokenMetadata
      if (t)
        map.set(t.symbol, {
          symbol: t.symbol,
          address: item.marketParams.collateralToken
        })
    })
    return Array.from(map.values()).map(token => ({
      value: token.address,
      keywords: [token.symbol],
      component: (
        <div className="flex items-center gap-2">
          <TokenIcon size="xs" symbol={token.symbol} />
          <span>{token.symbol}</span>
        </div>
      )
    }))
  }, [allPositions])

  const marketFilterOptions = useMemo(() => {
    if (!allPositions) return []
    const map = new Map<
      string,
      { marketId: string; collateralSymbol: string; loanSymbol: string }
    >()
    allPositions.forEach(item => {
      const collateralSymbol = item.collateralTokenMetadata?.symbol ?? ''
      const loanSymbol = item.loanTokenMetadata?.symbol ?? ''
      const key = `${collateralSymbol}/${loanSymbol}`
      if (!map.has(key)) {
        map.set(key, {
          marketId: item.marketId,
          collateralSymbol,
          loanSymbol
        })
      }
    })
    return Array.from(map.values()).map(market => {
      const MarketOption = () => (
        <div className="flex items-center gap-2">
          <TokenPairCell
            collateralSymbol={market.collateralSymbol}
            loanSymbol={market.loanSymbol}
          />
          <span className="text-xs">
            {market.collateralSymbol} / {market.loanSymbol}
          </span>
        </div>
      )

      return {
        value: market.marketId,
        keywords: [market.collateralSymbol, market.loanSymbol],
        component: <MarketOption />
      }
    })
  }, [allPositions])

  const borrowerFilterOptions = useMemo(() => {
    if (!allPositions) return []
    const uniqueBorrowers = new Set<string>()
    allPositions.forEach(item => {
      uniqueBorrowers.add(item.user)
    })
    return Array.from(uniqueBorrowers).map(borrower => ({
      value: borrower,
      keywords: [borrower, abbreviateAddress(borrower)],
      component: (
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs">{abbreviateAddress(borrower)}</span>
        </div>
      )
    }))
  }, [allPositions])

  const columns = useMemo<ColumnDef<LiquidationPosition>[]>(
    () => [
      {
        accessorKey: 'market',
        header: 'Market',
        cell: ({ row }) => {
          const { collateralTokenMetadata, loanTokenMetadata, marketId } = row.original
          return (
            <div className="flex items-center gap-2">
              <TokenPairCell
                collateralSymbol={collateralTokenMetadata?.symbol ?? ''}
                loanSymbol={loanTokenMetadata?.symbol ?? ''}
              />
              <div className="flex flex-col">
                <p
                  className="max-w-28 overflow-hidden text-xs text-ellipsis whitespace-nowrap"
                  title={`${collateralTokenMetadata?.symbol} / ${loanTokenMetadata?.symbol}`}
                >
                  {collateralTokenMetadata?.symbol} / {loanTokenMetadata?.symbol}
                </p>
                <MorphoLink className="w-min text-xs" marketId={marketId} chainId={chainId}>
                  {abbreviateAddress(marketId)}
                </MorphoLink>
              </div>
            </div>
          )
        }
      },
      {
        accessorKey: 'user',
        header: 'User address',
        cell: ({ row }) => {
          const { user } = row.original
          return (
            <EtherscanLink address={user} chainId={chainId} className="text-xs">
              {abbreviateAddress(user)}
            </EtherscanLink>
          )
        }
      },
      {
        accessorKey: 'borrowAssets',
        header: 'Borrow assets',
        cell: ({ row }) => {
          const { loanTokenMetadata, borrowAssets } = row.original
          return (
            <TokenAmountCell
              symbol={loanTokenMetadata?.symbol ?? ''}
              amount={borrowAssets}
              decimals={loanTokenMetadata?.decimals ?? 18}
            />
          )
        }
      },
      {
        accessorKey: 'collateral',
        header: 'Collateral assets',
        cell: ({ row }) => {
          const { collateralTokenMetadata, collateral } = row.original
          return (
            <TokenAmountCell
              symbol={collateralTokenMetadata?.symbol ?? ''}
              amount={collateral}
              decimals={collateralTokenMetadata?.decimals ?? 18}
            />
          )
        }
      },
      {
        accessorKey: 'healthFactor',
        header: 'Health factor',
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          const valueA = rowA.original.healthFactor ?? 0n
          const valueB = rowB.original.healthFactor ?? 0n
          return valueA < valueB ? -1 : valueA > valueB ? 1 : 0
        },
        cell: ({ row }) => (
          <span className="text-xs">{formatLTV(row.original.healthFactor, 18)}</span>
        )
      },
      {
        id: 'estimatedProfit',
        // `null` → `undefined` so `sortUndefined` can act on it: it short-circuits ahead of the
        // descending inversion, keeping unknown rows last in *both* directions. A comparator can't
        // express that.
        accessorFn: row => row.estimatedProfit ?? undefined,
        header: 'Estimated profit',
        enableSorting: true,
        sortUndefined: 'last',
        sortingFn: (rowA, rowB) => {
          const valueA = rowA.original.estimatedProfit ?? 0n
          const valueB = rowB.original.estimatedProfit ?? 0n
          return valueA < valueB ? -1 : valueA > valueB ? 1 : 0
        },
        cell: ({ row }) => {
          const profit = row.original.estimatedProfit
          if (profit === null) {
            return (
              <Text size="xs" className="text-typography-secondary">
                —
              </Text>
            )
          }

          const isNegative = profit < 0n
          const formattedValue = formatters.commas
            .readable()
            .unit('$')
            .digits(2)
            .of(isNegative ? -profit : profit, 18)

          return (
            <span className={cn('text-xs', isNegative && 'text-destructive-foreground')}>
              {isNegative ? '-' : ''}
              {formattedValue}
            </span>
          )
        }
      },
      {
        id: 'actions',
        header: '',
        meta: {
          align: 'right'
        },
        cell: ({ row }) => (
          <ActionsCell account={account} onLiquidate={() => onRowClick(row.original)} />
        )
      }
    ],
    [chainId, account, onRowClick]
  )

  const table = useReactTable({
    data: filteredPositions,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting
    },
    enableSorting: true,
    manualPagination: true
  })

  const { rows } = table.getRowModel()

  useEffect(() => {
    parentRef.current?.scrollTo({ top: 0, behavior: 'instant' })
  }, [collateralTokenFilter, loanTokenFilter, marketFilter])

  return (
    <div className="space-y-2">
      <div className="flex w-full items-center gap-4">
        {!selectedMarketId && (
          <MultiSelect
            label="Market"
            options={marketFilterOptions}
            values={marketFilter}
            onChange={setMarketFilter}
          />
        )}
        <MultiSelect
          label="Collateral token"
          options={collateralTokenFilterOptions}
          values={collateralTokenFilter}
          onChange={setCollateralTokenFilter}
        />
        <MultiSelect
          label="Loan token"
          options={loanTokenFilterOptions}
          values={loanTokenFilter}
          onChange={setLoanTokenFilter}
        />
        <MultiSelect
          label="Borrower"
          options={borrowerFilterOptions}
          values={borrowerFilter}
          onChange={setBorrowerFilter}
        />
        <MarketIdLookup marketId={selectedMarketId} onChange={setSelectedMarketId} />
        <div className="ml-auto flex items-center gap-3">
          {data && <CandidateListAge updatedAt={data.updatedAt} />}
          <Button
            variant="ghost"
            size="icon"
            className="text-typography-secondary rounded-full"
            onClick={() => refetch()}
            disabled={isFetching}
            tooltip="Refresh candidate list"
          >
            <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {data?.truncated && (
        <Alert variant="warning">
          {selectedMarketId
            ? 'Some positions in this market were not checked — discovery hit its page limit.'
            : 'Some positions were not checked — discovery hit its page limit. Open Market ID above to check one market in full.'}
        </Alert>
      )}

      <div className="bg-card overflow-hidden rounded-lg">
        <Table columnCount={table.getAllLeafColumns().length} className="text-sm">
          <DataTableHeader
            headerGroups={table.getHeaderGroups()}
            isSticky
            className="bg-card text-sm"
          />

          {isLoading || (!allPositions && !isError) ? (
            <TableBody maxHeight="400px" scrollable>
              {Array.from({ length: 50 }).map((_, i) => (
                <TableRow key={`loading-${i}`} className="mb-1 rounded-none" isLoading />
              ))}
            </TableBody>
          ) : (
            <TableBody maxHeight="400px" scrollable ref={parentRef}>
              {rows.map(row => (
                <TableRow key={row.id} className="hover:bg-muted/50">
                  {row.getVisibleCells().map(cell => (
                    <TableCell
                      key={cell.id}
                      className={cn(cell.column.columnDef.meta?.align === 'right' && 'text-right')}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>

        {/* Outside `TableBody`, whose children are grid cells. Four outcomes must stay distinct or
            they collapse into one blank table: broken discovery, a stale served list, nothing
            liquidatable, and filters matching nothing. Recovery copy never points at the market-ID
            input — targeted mode uses the same endpoint, so it fails whenever discovery does. */}
        {isError && allPositions && (
          <Alert variant="warning" className="m-4">
            Could not refresh. The amounts below are from the last successful load and may be out of
            date — retry before acting on them.
          </Alert>
        )}
        {isError && !allPositions && (
          <Alert variant="warning" className="m-4">
            {selectedMarketId
              ? 'Could not check this market. Retry with the refresh button above.'
              : 'Borrower discovery is unavailable, so no positions can be listed — this is not an empty result. Retry with the refresh button above.'}
          </Alert>
        )}
        {!isError && allPositions?.length === 0 && (
          <Text size="sm" className="text-typography-secondary p-4">
            {emptyResultMessage(data, Boolean(selectedMarketId))}
          </Text>
        )}
        {!isError && allPositions && allPositions.length > 0 && rows.length === 0 && (
          <Text size="sm" className="text-typography-secondary p-4">
            No positions match the current filters. Clear a filter to see the {allPositions.length}{' '}
            liquidatable position{allPositions.length === 1 ? '' : 's'} found.
          </Text>
        )}
      </div>

      {selectedPosition && account && (
        <LiquidationModal
          isOpen={modalOpen}
          onOpenChange={setModalOpen}
          position={selectedPosition}
          liquidatorAddress={account}
          onLiquidation={() => {
            setModalOpen(false)
            refetch()
          }}
        />
      )}
    </div>
  )
}
